import { yupResolver } from "@hookform/resolvers/yup";
import { Grid } from "@material-ui/core";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import backDNIDefault from "../../assets/img/backDNIDefault.png";
import userPhotoDefault from "../../assets/img/defaultUser.png";
import frontDNIDefault from "../../assets/img/frontDNIDefault.png";
import AccordionNav from "../../components/common/AccordionNav";
import ShowImage from "../../components/common/ShowImage";
import SimpleSelect from "../../components/common/SimpleSelect";
import TextFieldOutlined from "../../components/common/TextFieldOutlined";
import UserContext from "../../context/user-context";
import useInputUserChange from "../../hooks/useInputUserChange";
import useShowImage from "../../hooks/useShowImage";
import { useStore } from "../../hooks/useStore";
import { useTranslation } from "../../hooks/useTranslation";
import useUploadImage from "../../hooks/useUploadImage";
import { configFocusResetOff, configFocusResetOn } from "../../redux/actions/configActions";
import { UserFields } from "../../store/types/userTypes";
import { countryList } from "../../utils/countryCodes";
import { fileNames } from "../../utils/imageUtil";
import { CompanionTabIndex, EntrantTabIndex } from "../../utils/tabIndex";
import { entrantDetailSchema } from "../../utils/validations/EntrantDetailValidation";
import TypeEntrant from "./TypeEntrant/TypeEntrant";

const EntrantDetail = () => {
  const { state } = useContext(UserContext);
  const { configStore } = useStore();
  const globalDispatch = useDispatch();
  const { t, BundleKey } = useTranslation();
  const getImage = useShowImage();
  const { uploadNewPhoto } = useUploadImage();
  const [profilePhoto, setProfilePhoto] = useState<any>("");
  const [frontDNI, setFrontDNI] = useState<any>("");
  const [backDNI, setBackDNI] = useState<any>("");
  const { handleInputChange } = useInputUserChange();
  const firstnameRef = useRef();

  useEffect(() => {
    setProfilePhoto(getImage(state.facePhoto));
    //setFrontDNI(getImage(state.frontDNI));
    //setBackDNI(getImage(state.backDNI));
  }, [getImage, state.facePhoto]);

  const {
    register,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(entrantDetailSchema(t)),
    mode: "onBlur",
  });

  useEffect(() => {
    if (configStore.resetFocus) {
      const firstnameElement: any = firstnameRef.current;
      if (firstnameElement) firstnameElement.focus();
      globalDispatch(configFocusResetOff());
    }
  }, [configStore.resetFocus, globalDispatch]);

  const uploadNewProfilePhoto = (newPhoto: any) => {
    setProfilePhoto(newPhoto);
    uploadNewPhoto(newPhoto, UserFields.facePhoto, fileNames.PROFILE_IMAGE_FILENAME, !isFull());
  };

  const uploadNewFrontDniPhoto = (newPhoto: any) => {
    setFrontDNI(newPhoto);
    uploadNewPhoto(newPhoto, UserFields.frontDNI, fileNames.FRONT_DNI_FILENAME, !isFull());
  };

  const uploadNewBackDniPhoto = (newPhoto: any) => {
    setBackDNI(newPhoto);
    uploadNewPhoto(newPhoto, UserFields.backDNI, fileNames.BACK_DNI_FILENAME, !isFull());
  };

  const isFull = () => {
    return state.registrationType === "full";
  };

  const changeFocus = (event: any) => {
    globalDispatch(configFocusResetOn());
  };

  return (
    <AccordionNav title={t(BundleKey.DETAIL_PERSONALINF_TITLE)} mobile expanded={true}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <ShowImage
            title={t(BundleKey.DETAIL_PERSONALINF_PROFILEPHOTO)}
            defaultImage={userPhotoDefault}
            image={profilePhoto}
            tabIndex={EntrantTabIndex.hidden}
            uploadNewPhoto={uploadNewProfilePhoto}
            alt={"image1"}
            circle
            onFocus={changeFocus}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Grid container direction="column" spacing={3}>
            <Grid item>
              <TextFieldOutlined
                required
                name="firstname"
                type="text"
                label={t(BundleKey.DETAIL_PERSONALINF_FIRSTNAME)}
                value={state.firstname}
                registration={register("firstname")}
                inputRef={firstnameRef}
                onChange={(value) => handleInputChange("firstname", value)}
                InputProps={{
                  tabIndex: state.config?.companion ? CompanionTabIndex.name : EntrantTabIndex.name,
                }}
                autoFocus={true}
                disabled={state.config?.eventType === "out" && !state.config?.newEventOut}
                errors={errors.firstname?.message}
              />
            </Grid>
            <Grid item>
              <TextFieldOutlined
                required
                name="lastname"
                type="text"
                label={t(BundleKey.DETAIL_PERSONALINF_LASTNAME)}
                value={state.lastname}
                registration={register("lastname")}
                onChange={(value) => handleInputChange("lastname", value)}
                InputProps={{
                  tabIndex: state.config?.companion
                    ? CompanionTabIndex.surname
                    : EntrantTabIndex.surname,
                }}
                disabled={state.config?.eventType === "out" && !state.config?.newEventOut}
                errors={errors.lastname?.message}
              />
            </Grid>
            <Grid item>
              <SimpleSelect
                required
                id="countryCode"
                label={t(BundleKey.DETAIL_PERSONALINF_COUNTRY)}
                value={state.countryCode}
                onChange={(value) => handleInputChange("countryCode", value)}
                items={countryList}
                errors={errors.countryCode?.message}
                inputProps={{
                  tabIndex: EntrantTabIndex.hidden,
                  type: "text",
                }}
                disabled={state.config?.eventType === "out" && !state.config?.newEventOut}
              />
            </Grid>
            <Grid item>
              <TextFieldOutlined
                required
                name="dni"
                type="text"
                label={t(BundleKey.DETAIL_PERSONALINF_DNI)}
                value={state.dni}
                onChange={(value) => handleInputChange("dni", value)}
                registration={register("dni")}
                InputProps={{ tabIndex: EntrantTabIndex.hidden }}
                disabled={state.config?.eventType === "out" && !state.config?.newEventOut}
                errors={errors.dni?.message}
              />
              <TypeEntrant />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} md={4}>
          <Grid container spacing={3} style={{ height: "100%" }}>
            <Grid item xs={12} sm={6} md={12}>
              <ShowImage
                defaultImage={frontDNIDefault}
                image={frontDNI || getImage(state.frontDNI)}
                title={t(BundleKey.DETAIL_PERSONALINF_DNIFRONTPHOTO)}
                uploadNewPhoto={uploadNewFrontDniPhoto}
                tabIndex={EntrantTabIndex.hidden}
                alt={"frontDni"}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={12}>
              <ShowImage
                defaultImage={backDNIDefault}
                image={backDNI || getImage(state.backDNI)}
                title={t(BundleKey.DETAIL_PERSONALINF_DNIBACKPHOTO)}
                tabIndex={EntrantTabIndex.hidden}
                uploadNewPhoto={uploadNewBackDniPhoto}
                alt={"backDni"}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </AccordionNav>
  );
};

export default EntrantDetail;
